import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { ReportsCatalogSkeleton, ReportsGeneratingSkeleton } from '../../components/common/Skeleton';
import { PageFadeSection } from '../../components/common/PageFadeSection';
import {
  FileText, Download, Clock, Factory, Users, ClipboardList, RefreshCw,
  Calendar, Filter, ChevronDown, ChevronUp, BarChart3, AlertTriangle,
  CheckCircle, XCircle, Loader2, TrendingUp
} from 'lucide-react';
import {
  getReportCatalog, getTimeEntrySummary, getProductionTaskReport,
  getTeamPerformanceReport, getTaskAssignmentReport, getSyncHealthReport,
  getPersonalInsightsReport, getLeadershipInsightsReport,
  exportReportCsv
} from '../../api/reports.api';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  BarChart,
  Bar,
} from 'recharts';
import { parseUTC } from '../../utils/dateUtils';

const iconMap = { Clock, Factory, Users, ClipboardList, RefreshCw, FileText, BarChart3 };
const getIcon = (name) => iconMap[name] || FileText;

/* ═══════════════════════════════════════════════════════════ */
/*  Reports Page                                               */
/* ═══════════════════════════════════════════════════════════ */
const ReportsPage = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [catalog, setCatalog] = useState([]);
  const [selectedReport, setSelectedReport] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    weeksLookback: 8,
    teamId: '',
    employeeId: '',
    compareMode: 'default',
    highlightTopPerformers: false,
  });
  const [showFilters, setShowFilters] = useState(false);

  const buildReportParams = useCallback(() => {
    const params = {};
    if (filters.startDate) params.startDate = filters.startDate;
    if (filters.endDate) params.endDate = filters.endDate;
    if (filters.weeksLookback) params.weeksLookback = Number(filters.weeksLookback) || 8;
    if (filters.teamId?.trim()) params.teamId = filters.teamId.trim();
    if (filters.employeeId?.trim()) params.employeeId = filters.employeeId.trim();
    if (filters.compareMode) params.compareMode = filters.compareMode;
    if (filters.highlightTopPerformers) params.highlightTopPerformers = true;
    return params;
  }, [filters]);

  // Fetch catalog
  useEffect(() => {
    (async () => {
      try {
        const res = await getReportCatalog();
        setCatalog(res.data?.data || []);
      } catch { }
      setCatalogLoading(false);
    })();
  }, []);

  // Fetch report data
  const loadReport = useCallback(async (key) => {
    setSelectedReport(key);
    setReportData(null);
    setLoading(true);
    try {
      const params = buildReportParams();
      let res;
      switch (key) {
        case 'personal-insights': res = await getPersonalInsightsReport(params); break;
        case 'leadership-insights': res = await getLeadershipInsightsReport(params); break;
        case 'time-entry-summary': res = await getTimeEntrySummary(params); break;
        case 'production-task': res = await getProductionTaskReport(params); break;
        case 'team-performance': res = await getTeamPerformanceReport(params); break;
        case 'task-assignment': res = await getTaskAssignmentReport(params); break;
        case 'sync-health': res = await getSyncHealthReport(params); break;
        default: return;
      }
      setReportData(res.data?.data);
    } catch (err) {
      console.error('Report load error:', err);
    }
    setLoading(false);
  }, [buildReportParams]);

  // Export CSV
  const handleExport = async () => {
    if (!selectedReport) return;
    setExporting(true);
    try {
      const params = buildReportParams();
      const res = await exportReportCsv(selectedReport, params);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${selectedReport}_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export error:', err);
    }
    setExporting(false);
  };

  /* ─── Stat Card ─── */
  const StatCard = ({ icon: Icon, label, value, sub, color = 'indigo' }) => (
    <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 border border-gray-100 dark:border-gray-700/50 shadow-sm hover:shadow-md transition-shadow">
      <div className="flex items-center gap-3 mb-2">
        <div className={`p-2 rounded-xl bg-${color}-50 dark:bg-${color}-500/10`}>
          <Icon className={`w-5 h-5 text-${color}-500`} />
        </div>
        <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">{label}</p>
      </div>
      <p className="text-2xl font-bold text-gray-900 dark:text-white">{value}</p>
      {sub && <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{sub}</p>}
    </div>
  );

  /* ─── Report Renderers ─── */
  const renderTimeEntrySummary = (d) => (
    <>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
        <StatCard icon={FileText} label={t('pages.reports.statTotalEntries')} value={d.totalEntries} color="indigo" />
        <StatCard icon={Clock} label={t('pages.reports.statTotalHours')} value={d.totalHours?.toFixed(1)} color="blue" />
        <StatCard icon={TrendingUp} label={t('pages.reports.statRunning')} value={d.runningTimers} color="amber" />
        <StatCard icon={CheckCircle} label={t('pages.reports.statSynced')} value={d.syncedEntries} color="green" />
        <StatCard icon={Clock} label={t('pages.reports.statPending')} value={d.pendingEntries} color="yellow" />
        <StatCard icon={XCircle} label={t('pages.reports.statFailed')} value={d.failedEntries} color="red" />
      </div>

      {/* By Time Type */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 border border-gray-100 dark:border-gray-700/50 mb-4">
        <h3 className="text-sm font-bold text-gray-700 dark:text-gray-300 mb-3">{t('pages.reports.byTimeType')}</h3>
        <div className="space-y-2">
          {d.byTimeType?.map((row, i) => (
            <div key={i} className="flex items-center justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-400">{row.timeTypeCode}</span>
              <div className="flex items-center gap-4">
                <div className="w-40 h-2 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                  <div className="h-full bg-indigo-500 rounded-full" style={{ width: `${d.totalEntries > 0 ? (row.count / d.totalEntries * 100) : 0}%` }} />
                </div>
                <span className="text-sm font-semibold text-gray-700 dark:text-gray-300 w-20 text-right">{row.count} ({row.totalHours?.toFixed(1)}h)</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Top Employees */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 border border-gray-100 dark:border-gray-700/50">
        <h3 className="text-sm font-bold text-gray-700 dark:text-gray-300 mb-3">{t('pages.reports.topEmployees')}</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 dark:border-gray-700">
                <th className="text-left py-2 text-gray-500 dark:text-gray-400 font-medium">{t('pages.reports.colEmployee')}</th>
                <th className="text-right py-2 text-gray-500 dark:text-gray-400 font-medium">{t('pages.reports.colEntries')}</th>
                <th className="text-right py-2 text-gray-500 dark:text-gray-400 font-medium">{t('pages.reports.colHours')}</th>
                <th className="text-right py-2 text-gray-500 dark:text-gray-400 font-medium">{t('pages.reports.colRunning')}</th>
              </tr>
            </thead>
            <tbody>
              {d.byEmployee?.slice(0, 15).map((e, i) => (
                <tr key={i} className="border-b border-gray-50 dark:border-gray-700/30">
                  <td className="py-2 text-gray-700 dark:text-gray-300">{e.employeeName}</td>
                  <td className="py-2 text-right text-gray-600 dark:text-gray-400">{e.entryCount}</td>
                  <td className="py-2 text-right font-semibold text-gray-700 dark:text-gray-300">{e.totalHours?.toFixed(1)}</td>
                  <td className="py-2 text-right">{e.runningTimers > 0 && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-amber-50 dark:bg-amber-500/10 text-amber-600">{e.runningTimers}</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );

  const renderProductionTask = (d) => (
    <>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
        <StatCard icon={Factory} label={t('pages.reports.statProdLots')} value={d.totalProductionLots} color="indigo" />
        <StatCard icon={ClipboardList} label={t('pages.reports.statProdTasks')} value={d.totalProductionTasks} color="blue" />
        <StatCard icon={BarChart3} label={t('pages.reports.statActivities')} value={d.totalActivities} color="purple" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        {/* Lots by Status */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 border border-gray-100 dark:border-gray-700/50">
          <h3 className="text-sm font-bold text-gray-700 dark:text-gray-300 mb-3">Lots by Lifecycle Status</h3>
          <div className="space-y-2">
            {d.lotsByLifecycleStatus?.map((s, i) => (
              <div key={i} className="flex items-center justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-400">{s.statusDescription}</span>
                <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">{s.count} ({s.percentage}%)</span>
              </div>
            ))}
          </div>
        </div>
        {/* Tasks by Status */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 border border-gray-100 dark:border-gray-700/50">
          <h3 className="text-sm font-bold text-gray-700 dark:text-gray-300 mb-3">{t('pages.reports.tasksByLifecycle')}</h3>
          <div className="space-y-2">
            {d.tasksByLifecycleStatus?.map((s, i) => (
              <div key={i} className="flex items-center justify-between">
                <span className="text-sm text-gray-600 dark:text-gray-400">{s.statusDescription}</span>
                <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">{s.count} ({s.percentage}%)</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Lot summaries table */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 border border-gray-100 dark:border-gray-700/50">
        <h3 className="text-sm font-bold text-gray-700 dark:text-gray-300 mb-3">{t('pages.reports.lotSummaries')}</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 dark:border-gray-700">
                <th className="text-left py-2 text-gray-500 dark:text-gray-400 font-medium">{t('pages.reports.colLotId')}</th>
                <th className="text-left py-2 text-gray-500 dark:text-gray-400 font-medium">{t('pages.reports.colProduct')}</th>
                <th className="text-left py-2 text-gray-500 dark:text-gray-400 font-medium">{t('pages.reports.colStatus')}</th>
                <th className="text-right py-2 text-gray-500 dark:text-gray-400 font-medium">{t('pages.reports.colQty')}</th>
                <th className="text-right py-2 text-gray-500 dark:text-gray-400 font-medium">{t('pages.reports.colTasks')}</th>
              </tr>
            </thead>
            <tbody>
              {d.lotSummaries?.slice(0, 20).map((l, i) => (
                <tr key={i} className="border-b border-gray-50 dark:border-gray-700/30">
                  <td className="py-2 text-gray-700 dark:text-gray-300 font-mono text-xs">{l.productionLotId}</td>
                  <td className="py-2 text-gray-600 dark:text-gray-400">{l.productDescription || '—'}</td>
                  <td className="py-2"><span className="px-2 py-0.5 rounded-full text-xs bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400">{l.lifeCycleStatus}</span></td>
                  <td className="py-2 text-right text-gray-600 dark:text-gray-400">{l.completedQuantity ?? 0}/{l.totalQuantity ?? 0}</td>
                  <td className="py-2 text-right font-semibold text-gray-700 dark:text-gray-300">{l.taskCount}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );

  const renderTeamPerformance = (d) => (
    <div className="space-y-4">
      {d.teams?.map((team, i) => (
        <div key={i} className="bg-white dark:bg-gray-800 rounded-2xl p-5 border border-gray-100 dark:border-gray-700/50">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-base font-bold text-gray-800 dark:text-white">{team.teamName}</h3>
              <p className="text-xs text-gray-500 dark:text-gray-400">{team.department || t('pages.reports.noDept')} · {t('pages.reports.lead')} {team.leadName} {team.pmName ? `· ${t('pages.reports.pm')} ${team.pmName}` : ''}</p>
            </div>
            <div className="text-right">
              <p className="text-lg font-bold text-indigo-600 dark:text-indigo-400">{team.completionRate}%</p>
              <p className="text-[10px] text-gray-500 dark:text-gray-400 uppercase">{t('pages.reports.completion')}</p>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
            <div className="text-center p-2 bg-gray-50 dark:bg-gray-700/30 rounded-xl">
              <p className="text-lg font-bold text-gray-800 dark:text-white">{team.memberCount}</p>
              <p className="text-[10px] text-gray-500 dark:text-gray-400">{t('pages.reports.members')}</p>
            </div>
            <div className="text-center p-2 bg-blue-50 dark:bg-blue-500/10 rounded-xl">
              <p className="text-lg font-bold text-blue-600 dark:text-blue-400">{team.totalTasksAssigned}</p>
              <p className="text-[10px] text-gray-500 dark:text-gray-400">{t('pages.reports.colTasks')}</p>
            </div>
            <div className="text-center p-2 bg-green-50 dark:bg-green-500/10 rounded-xl">
              <p className="text-lg font-bold text-green-600 dark:text-green-400">{team.tasksCompleted}</p>
              <p className="text-[10px] text-gray-500 dark:text-gray-400">{t('pages.reports.doneLabel')}</p>
            </div>
            <div className="text-center p-2 bg-red-50 dark:bg-red-500/10 rounded-xl">
              <p className="text-lg font-bold text-red-600 dark:text-red-400">{team.tasksOverdue}</p>
              <p className="text-[10px] text-gray-500 dark:text-gray-400">{t('pages.reports.overdueLabel')}</p>
            </div>
            <div className="text-center p-2 bg-indigo-50 dark:bg-indigo-500/10 rounded-xl">
              <p className="text-lg font-bold text-indigo-600 dark:text-indigo-400">{team.totalHoursLogged?.toFixed(1)}</p>
              <p className="text-[10px] text-gray-500 dark:text-gray-400">{t('pages.reports.colHours')}</p>
            </div>
          </div>

          {/* Members */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 dark:border-gray-700">
                  <th className="text-left py-1.5 text-gray-500 dark:text-gray-400 font-medium text-xs">{t('pages.reports.colMember')}</th>
                  <th className="text-right py-1.5 text-gray-500 dark:text-gray-400 font-medium text-xs">{t('pages.reports.colTasks')}</th>
                  <th className="text-right py-1.5 text-gray-500 dark:text-gray-400 font-medium text-xs">{t('pages.reports.colDone')}</th>
                  <th className="text-right py-1.5 text-gray-500 dark:text-gray-400 font-medium text-xs">{t('pages.reports.inProgress')}</th>
                  <th className="text-right py-1.5 text-gray-500 dark:text-gray-400 font-medium text-xs">{t('pages.reports.overdueLabel')}</th>
                  <th className="text-right py-1.5 text-gray-500 dark:text-gray-400 font-medium text-xs">{t('pages.reports.colHours')}</th>
                </tr>
              </thead>
              <tbody>
                {team.members?.map((m, j) => (
                  <tr key={j} className="border-b border-gray-50 dark:border-gray-700/30">
                    <td className="py-1.5 text-gray-700 dark:text-gray-300">{m.fullName}</td>
                    <td className="py-1.5 text-right text-gray-600 dark:text-gray-400">{m.tasksAssigned}</td>
                    <td className="py-1.5 text-right text-green-600 dark:text-green-400">{m.tasksCompleted}</td>
                    <td className="py-1.5 text-right text-blue-600 dark:text-blue-400">{m.tasksInProgress}</td>
                    <td className="py-1.5 text-right">{m.tasksOverdue > 0 ? <span className="text-red-600">{m.tasksOverdue}</span> : <span className="text-gray-400">0</span>}</td>
                    <td className="py-1.5 text-right font-semibold text-gray-700 dark:text-gray-300">{m.hoursLogged?.toFixed(1)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
      {(!d.teams || d.teams.length === 0) && (
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">{t('pages.reports.noTeamsScope')}</div>
      )}
    </div>
  );

  const renderTaskAssignment = (d) => (
    <>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
        <StatCard icon={ClipboardList} label={t('pages.reports.totalAssignments')} value={d.totalAssignments} color="indigo" />
        <StatCard icon={Users} label={t('pages.reports.totalAssignees')} value={d.totalAssignees} color="blue" />
        <StatCard icon={AlertTriangle} label={t('pages.reports.overdueLabel')} value={d.overdueTasks?.length || 0} color="red" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 border border-gray-100 dark:border-gray-700/50">
          <h3 className="text-sm font-bold text-gray-700 dark:text-gray-300 mb-3">{t('pages.reports.byStatus')}</h3>
          {d.byStatus?.map((s, i) => (
            <div key={i} className="flex items-center justify-between mb-1">
              <span className="text-sm text-gray-600 dark:text-gray-400">{s.statusDescription}</span>
              <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">{s.count} ({s.percentage}%)</span>
            </div>
          ))}
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 border border-gray-100 dark:border-gray-700/50">
          <h3 className="text-sm font-bold text-gray-700 dark:text-gray-300 mb-3">{t('pages.reports.byPriority')}</h3>
          {d.byPriority?.map((p, i) => (
            <div key={i} className="flex items-center justify-between mb-1">
              <span className="text-sm text-gray-600 dark:text-gray-400">{p.statusDescription}</span>
              <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">{p.count} ({p.percentage}%)</span>
            </div>
          ))}
        </div>
      </div>

      {/* Overdue table */}
      {d.overdueTasks?.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 border border-red-100 dark:border-red-500/20">
          <h3 className="text-sm font-bold text-red-600 dark:text-red-400 mb-3">{t('pages.reports.overdueTasksTitle')}</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-red-50 dark:border-red-500/10">
                  <th className="text-left py-2 text-gray-500 dark:text-gray-400 font-medium">{t('pages.reports.colProductionLot')}</th>
                  <th className="text-left py-2 text-gray-500 dark:text-gray-400 font-medium">{t('pages.reports.colAssignee')}</th>
                  <th className="text-left py-2 text-gray-500 dark:text-gray-400 font-medium">{t('pages.reports.colStatus')}</th>
                  <th className="text-right py-2 text-gray-500 dark:text-gray-400 font-medium">{t('pages.reports.daysOverdue')}</th>
                </tr>
              </thead>
              <tbody>
                {d.overdueTasks?.map((o, i) => (
                  <tr key={i} className="border-b border-gray-50 dark:border-gray-700/30">
                    <td className="py-2 text-gray-700 dark:text-gray-300">{o.productionLotName}</td>
                    <td className="py-2 text-gray-600 dark:text-gray-400">{o.assigneeName}</td>
                    <td className="py-2"><span className="px-2 py-0.5 rounded-full text-xs bg-amber-50 dark:bg-amber-500/10 text-amber-600">{o.status}</span></td>
                    <td className="py-2 text-right font-bold text-red-600">{o.daysOverdue}d</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );

  const renderSyncHealth = (d) => (
    <>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <StatCard icon={FileText} label={t('pages.reports.statTotalEntries')} value={d.totalTimeEntries} color="indigo" />
        <StatCard icon={CheckCircle} label={t('pages.reports.statSynced')} value={d.synced} sub={`${d.syncPercentage}%`} color="green" />
        <StatCard icon={Clock} label={t('pages.reports.statPending')} value={d.pending} color="yellow" />
        <StatCard icon={XCircle} label={t('pages.reports.statFailed')} value={d.failed} color="red" />
      </div>

      {/* Sync progress bar */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 border border-gray-100 dark:border-gray-700/50 mb-4">
        <div className="flex justify-between mb-2">
          <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">{t('pages.reports.syncProgress')}</span>
          <span className="text-sm font-bold text-indigo-600 dark:text-indigo-400">{d.syncPercentage}%</span>
        </div>
        <div className="h-3 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
          <div className="h-full rounded-full bg-gradient-to-r from-indigo-500 to-green-500 transition-all" style={{ width: `${d.syncPercentage || 0}%` }} />
        </div>
      </div>

      {/* Recent failures */}
      {d.recentFailures?.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 border border-red-100 dark:border-red-500/20">
          <h3 className="text-sm font-bold text-red-600 dark:text-red-400 mb-3">{t('pages.reports.recentFailures')}</h3>
          <div className="space-y-2">
            {d.recentFailures?.map((f, i) => (
              <div key={i} className="flex items-start gap-3 p-2 bg-red-50/50 dark:bg-red-500/5 rounded-lg">
                <XCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm text-gray-700 dark:text-gray-300">{f.employeeName} — {parseUTC(f.date).toLocaleDateString()}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{f.errorMessage || t('pages.reports.noErrorMsg')}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );

  const renderPersonalInsights = (d) => {
    const chartRows = (d.weeks || []).map((w) => ({
      label: w.weekLabel,
      hours: Number(w.hours) || 0,
      tasks: Number(w.tasksCompleted) || 0,
    }));
    return (
      <>
        {d.note && (
          <div className="mb-4 p-4 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-100 dark:border-amber-800/40 text-sm text-amber-900 dark:text-amber-200">
            {d.note}
          </div>
        )}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <StatCard icon={TrendingUp} label={t('pages.reports.hoursTrend')} value={d.hoursTrendLabel} color="indigo" />
          <StatCard icon={Clock} label={t('pages.reports.slopePerWeek')} value={Number(d.hoursSlopePerWeek).toFixed(2)} color="blue" />
          <StatCard icon={BarChart3} label={t('pages.reports.nextWeekFit')} value={`${Number(d.predictedNextWeekHours).toFixed(1)}h`} color="purple" />
          <StatCard icon={Users} label={t('pages.reports.lastPriorAvg')} value={`${Number(d.lastWeekHours).toFixed(1)} / ${Number(d.priorWeeksAverageHours).toFixed(1)}`} color="green" />
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 border border-gray-100 dark:border-gray-700/50 mb-4">
          <h3 className="text-sm font-bold text-gray-700 dark:text-gray-300 mb-3">{t('pages.reports.weeklyHoursCompletions')}</h3>
          {chartRows.length > 0 ? (
            <div className="h-72 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartRows} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-gray-200 dark:stroke-gray-700" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                  <YAxis yAxisId="left" tick={{ fontSize: 11 }} width={40} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} width={32} />
                  <Tooltip contentStyle={{ borderRadius: 12 }} />
                  <Legend />
                  <Line yAxisId="left" type="monotone" dataKey="hours" name={t('pages.reports.colHours')} stroke="#4f46e5" strokeWidth={2} dot />
                  <Line yAxisId="right" type="monotone" dataKey="tasks" name={t('pages.reports.tasksCompleted')} stroke="#db2777" strokeWidth={2} dot />
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <p className="text-sm text-gray-500 py-8 text-center">{t('pages.reports.noWeeklyBuckets')}</p>
          )}
        </div>

        <div className="rounded-2xl p-5 border border-indigo-100 dark:border-indigo-900/40 bg-indigo-50/60 dark:bg-indigo-950/30">
          <h3 className="text-sm font-bold text-indigo-800 dark:text-indigo-200 mb-2">{t('pages.reports.selfReflection')}</h3>
          <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-wrap">{d.selfReflectionSummary}</p>
        </div>
      </>
    );
  };

  const renderLeadershipInsights = (d) => (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
        <span className="font-semibold text-gray-600 dark:text-gray-300">{t('pages.reports.viewer')}</span>
        <span>{d.viewerName}</span>
        <span className="mx-1">·</span>
        <span>{d.viewerRole}</span>
        <span className="mx-1">·</span>
        <span>{t('pages.reports.compareMode')} <span className="font-medium text-gray-700 dark:text-gray-300">{d.compareMode}</span></span>
      </div>

      {d.viewerVsTeamTop && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <StatCard icon={Users} label={t('pages.reports.yourHoursInScope')} value={Number(d.viewerVsTeamTop.viewerTotalHours).toFixed(1)} color="indigo" />
          <StatCard icon={TrendingUp} label={t('pages.reports.topPerformer')} value={d.viewerVsTeamTop.topPerformerName} sub={t('pages.reports.loggedSuffix', { hours: Number(d.viewerVsTeamTop.topPerformerHours).toFixed(1) })} color="green" />
          <StatCard icon={BarChart3} label={t('pages.reports.gapVsTop')} value={`${d.viewerVsTeamTop.hoursGapPercent}%`} color="amber" />
        </div>
      )}

      {!d.teams?.length && (
        <p className="text-center text-gray-500 dark:text-gray-400 py-12">{t('pages.reports.noTeamsFilters')}</p>
      )}

      {d.teams?.map((team, i) => {
        const barData = (team.teamWeeklyTrend || []).map((w) => ({
          label: w.weekLabel,
          hours: Number(w.hours) || 0,
          tasks: Number(w.tasksCompleted) || 0,
        }));
        return (
          <div key={team.teamId || i} className="bg-white dark:bg-gray-800 rounded-2xl p-5 border border-gray-100 dark:border-gray-700/50 space-y-4">
            <h3 className="text-base font-bold text-gray-800 dark:text-white">{team.teamName}</h3>

            {barData.length > 0 ? (
              <div className="h-56 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={barData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-gray-200 dark:stroke-gray-700" />
                    <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} width={32} />
                    <Tooltip contentStyle={{ borderRadius: 12 }} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="hours" name={t('pages.reports.teamHours')} fill="#6366f1" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="tasks" name={t('pages.reports.tasksCompleted')} fill="#ec4899" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : null}

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 dark:border-gray-700">
                    <th className="text-left py-2 text-gray-500 dark:text-gray-400 font-medium">{t('pages.reports.colMember')}</th>
                    <th className="text-right py-2 text-gray-500 dark:text-gray-400 font-medium">{t('pages.reports.colRank')}</th>
                    <th className="text-right py-2 text-gray-500 dark:text-gray-400 font-medium">{t('pages.reports.colHours')}</th>
                    <th className="text-right py-2 text-gray-500 dark:text-gray-400 font-medium">{t('pages.reports.colDone')}</th>
                    <th className="text-right py-2 text-gray-500 dark:text-gray-400 font-medium">{t('pages.reports.colSlope')}</th>
                    <th className="text-left py-2 text-gray-500 dark:text-gray-400 font-medium">{t('pages.reports.colTrend')}</th>
                    <th className="text-left py-2 text-gray-500 dark:text-gray-400 font-medium">{t('pages.reports.colFlags')}</th>
                  </tr>
                </thead>
                <tbody>
                  {team.members?.map((m, j) => (
                    <tr key={m.userId || j} className={`border-b border-gray-50 dark:border-gray-700/30 ${m.isViewer ? 'bg-indigo-50/50 dark:bg-indigo-950/20' : ''}`}>
                      <td className="py-2 text-gray-800 dark:text-gray-200">{m.fullName}</td>
                      <td className="py-2 text-right text-gray-600 dark:text-gray-400">{m.rankInTeam}</td>
                      <td className="py-2 text-right font-semibold text-gray-700 dark:text-gray-300">{Number(m.totalHours).toFixed(1)}</td>
                      <td className="py-2 text-right text-gray-600 dark:text-gray-400">{m.tasksCompleted}</td>
                      <td className="py-2 text-right text-xs text-gray-600 dark:text-gray-400">{Number(m.hoursSlopePerWeek).toFixed(2)}</td>
                      <td className="py-2"><span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200">{m.trendLabel}</span></td>
                      <td className="py-2">
                        <span className="flex flex-wrap gap-1">
                          {m.isTopPerformer && <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">{t('pages.reports.top')}</span>}
                          {m.isViewer && <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-200">{t('pages.reports.you')}</span>}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}
    </div>
  );

  const renderReport = () => {
    if (!reportData) return null;
    switch (selectedReport) {
      case 'personal-insights': return renderPersonalInsights(reportData);
      case 'leadership-insights': return renderLeadershipInsights(reportData);
      case 'time-entry-summary': return renderTimeEntrySummary(reportData);
      case 'production-task': return renderProductionTask(reportData);
      case 'team-performance': return renderTeamPerformance(reportData);
      case 'task-assignment': return renderTaskAssignment(reportData);
      case 'sync-health': return renderSyncHealth(reportData);
      default: return null;
    }
  };

  return (
    <div className="space-y-6">
      <PageFadeSection>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{t('pages.reports.title')}</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{t('pages.reports.subtitle')}</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 px-4 py-2 rounded-xl border border-gray-200 dark:border-gray-600 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
          >
            <Filter className="w-4 h-4" />
            {t('pages.reports.filters')}
            {showFilters ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </button>
          {selectedReport && (
            <button
              onClick={handleExport}
              disabled={exporting || !reportData}
              className="flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-indigo-500 to-indigo-600 text-white text-sm font-medium hover:from-indigo-600 hover:to-indigo-700 disabled:opacity-50 transition-colors shadow-sm"
            >
              {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              {t('pages.reports.exportCsv')}
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 border border-gray-100 dark:border-gray-700/50 flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('pages.reports.startDate')}</label>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => setFilters(f => ({ ...f, startDate: e.target.value }))}
              className="px-3 py-2 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-700 dark:text-gray-300"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('pages.reports.endDate')}</label>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => setFilters(f => ({ ...f, endDate: e.target.value }))}
              className="px-3 py-2 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-700 dark:text-gray-300"
            />
          </div>
          {(selectedReport === 'personal-insights' || selectedReport === 'leadership-insights') && (
            <div>
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('pages.reports.weeksLookback')}</label>
              <input
                type="number"
                min={2}
                max={26}
                value={filters.weeksLookback}
                onChange={(e) => setFilters(f => ({ ...f, weeksLookback: e.target.value }))}
                className="px-3 py-2 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-700 dark:text-gray-300 w-24"
              />
            </div>
          )}
          {selectedReport === 'leadership-insights' && (
            <>
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('pages.reports.teamIdOptional')}</label>
                <input
                  type="text"
                  placeholder={t('pages.reports.guidPlaceholder')}
                  value={filters.teamId}
                  onChange={(e) => setFilters(f => ({ ...f, teamId: e.target.value }))}
                  className="px-3 py-2 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-700 dark:text-gray-300 w-44"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('pages.reports.memberUserIdOptional')}</label>
                <input
                  type="text"
                  placeholder={t('pages.reports.userIdPlaceholder')}
                  value={filters.employeeId}
                  onChange={(e) => setFilters(f => ({ ...f, employeeId: e.target.value }))}
                  className="px-3 py-2 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-700 dark:text-gray-300 w-44"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('pages.reports.compare')}</label>
                <select
                  value={filters.compareMode}
                  onChange={(e) => setFilters(f => ({ ...f, compareMode: e.target.value }))}
                  className="px-3 py-2 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm text-gray-700 dark:text-gray-300"
                >
                  <option value="default">{t('pages.reports.compareDefault')}</option>
                  <option value="top_performers">{t('pages.reports.compareTop')}</option>
                </select>
              </div>
              <label className="flex items-center gap-2 cursor-pointer pb-2">
                <input
                  type="checkbox"
                  checked={filters.highlightTopPerformers}
                  onChange={(e) => setFilters(f => ({ ...f, highlightTopPerformers: e.target.checked }))}
                  className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
                <span className="text-xs font-medium text-gray-600 dark:text-gray-300">{t('pages.reports.highlightTop3')}</span>
              </label>
            </>
          )}
          {selectedReport && (
            <button
              onClick={() => loadReport(selectedReport)}
              className="px-4 py-2 rounded-xl bg-indigo-100 dark:bg-indigo-500/20 text-indigo-600 dark:text-indigo-400 text-sm font-medium hover:bg-indigo-200 dark:hover:bg-indigo-500/30 transition-colors"
            >
              {t('pages.reports.apply')}
            </button>
          )}
        </div>
      )}
      </PageFadeSection>

      {/* Report Catalog */}
      {catalogLoading ? (
        <PageFadeSection>
          <ReportsCatalogSkeleton />
        </PageFadeSection>
      ) : (
        <PageFadeSection>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
          {catalog.map((item) => {
            const Icon = getIcon(item.icon);
            const active = selectedReport === item.reportKey;
            return (
              <button
                key={item.reportKey}
                onClick={() => loadReport(item.reportKey)}
                className={`text-left p-4 rounded-2xl border transition-all duration-200
                  ${active
                    ? 'bg-gradient-to-br from-indigo-500 to-indigo-600 text-white border-indigo-500 shadow-lg shadow-indigo-500/25'
                    : 'bg-white dark:bg-gray-800 border-gray-100 dark:border-gray-700/50 hover:border-indigo-300 dark:hover:border-indigo-500/30 hover:shadow-md'
                  }`}
              >
                <Icon className={`w-6 h-6 mb-2 ${active ? 'text-white/90' : 'text-indigo-500'}`} />
                <p className={`text-sm font-bold ${active ? 'text-white' : 'text-gray-800 dark:text-white'}`}>{item.title}</p>
                <p className={`text-xs mt-1 ${active ? 'text-white/70' : 'text-gray-500 dark:text-gray-400'}`}>{item.description}</p>
              </button>
            );
          })}
        </div>
        </PageFadeSection>
      )}

      {/* Report Content */}
      {loading ? (
        <PageFadeSection>
          <ReportsGeneratingSkeleton />
        </PageFadeSection>
      ) : (
        reportData && (
          <PageFadeSection>
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-gray-800 dark:text-white">
                {catalog.find(c => c.reportKey === selectedReport)?.title}
              </h2>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Generated {parseUTC(reportData.generatedAt || Date.now()).toLocaleString()}
              </p>
            </div>
            {renderReport()}
          </div>
          </PageFadeSection>
        )
      )}

      {/* Empty State */}
      {!loading && !reportData && !selectedReport && (
        <PageFadeSection>
        <div className="text-center py-16">
          <BarChart3 className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300">{t('pages.reports.selectReport')}</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{t('pages.reports.selectReportHint')}</p>
        </div>
        </PageFadeSection>
      )}
    </div>
  );
};

export default ReportsPage;
